// @givmo/app-admin — auth-screen validation schemas (Zod) + the password-rule predicates.
//
// Ports the webapp auth validation VERBATIM (lib/core/helper/regexes.dart + the screens'
// `_isButtonActive`/`_onSubmit` gates) into Zod schemas consumed by `useGivmoForm`. The Flutter
// screens rendered an `isActive: true` always-tappable button and re-gated at click time as a
// Flutter/Playwright paint-race workaround; React re-renders synchronously, so we keep the
// VALIDATION LOGIC and drop the workaround — buttons are normal disabled-until-valid. The
// password screen still shows the 3 LIVE rule indicators (each derived from the predicates here),
// independent of the submit gate.

import { z } from 'zod';

// NF-WEB6 (cut 41 review): never ask the page to evaluate a string. Zod 4 probes `new Function("")`
// when the first object schema is BUILT; the admin Hosting CSP has no 'unsafe-eval', so the browser
// refuses it and logs a `script-src` violation on every load. `@givmo/payments` turns the probe off
// before its own schemas, but in this app's bundle the schemas below are built before that package
// loads, so this module turns it off first. It is global, and it must run before the first schema.
z.config({ jitless: true });

// ── Regexes (verbatim from webapp `Regexes`, regexes.dart) ────────────────────────────────────────

/**
 * Email format. Verbatim from `Regexes.emailValid` (regexes.dart). The webapp inline error on a
 * non-match is `Invalid email format` (no network call fires on a malformed email).
 */
export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/** Letters-only name (rejects spaces / hyphens / accents). Verbatim `Regexes.onlyLetters`. */
export const NAME_REGEX = /^[a-zA-Z]+$/;

/** "Has both letters and numbers". Verbatim `Regexes.hasLettersAndNumbers`. */
export const HAS_LETTERS_AND_NUMBERS_REGEX = /^(?=.*[A-Za-z])(?=.*\d).+$/;

/** "Has at least one symbol". Verbatim `Regexes.atLeastOneSymbol`. */
export const HAS_SYMBOL_REGEX = /^(?=.*\W).+$/;

// ── Verbatim error strings ───────────────────────────────────────────────────────────────────────

export const INVALID_EMAIL_MESSAGE = 'Invalid email format';
export const INVALID_NAME_MESSAGE = 'Please enter a valid name using only letters';
export const INVALID_EMPLOYEE_COUNT_MESSAGE = 'Please enter a valid numeric number of employees';

// ── Password-rule predicates (the 3 live indicators) ──────────────────────────────────────────────
// Mirror the webapp `_isActiveButton`: length > 7, hasLettersAndNumbers, atLeastOneSymbol.

export function passwordHasMinLength(password: string): boolean {
  return password.length > 7;
}

export function passwordHasLettersAndNumbers(password: string): boolean {
  return HAS_LETTERS_AND_NUMBERS_REGEX.test(password);
}

export function passwordHasSymbol(password: string): boolean {
  return HAS_SYMBOL_REGEX.test(password);
}

/** All three password rules satisfied (the webapp submit gate, sans the email term). */
export function passwordSatisfiesAllRules(password: string): boolean {
  return (
    passwordHasMinLength(password) &&
    passwordHasLettersAndNumbers(password) &&
    passwordHasSymbol(password)
  );
}

// ── Schemas ────────────────────────────────────────────────────────────────────────────────────────

/** Sign-in: email (format) + password (non-empty) + rememberMe. */
export const signInSchema = z.object({
  email: z.string().regex(EMAIL_REGEX, INVALID_EMAIL_MESSAGE),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean(),
});
export type SignInFormValues = z.infer<typeof signInSchema>;

/** Forgot-password: email only (format). */
export const forgotPasswordSchema = z.object({
  email: z.string().regex(EMAIL_REGEX, INVALID_EMAIL_MESSAGE),
});
export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

/**
 * Sign-up details: first/last name (letters-only), company name (non-empty), employee count (parses
 * to an int). `numberOfEmployees` is held as a string in the input and transformed to the parsed int
 * on output — the webapp's `int.tryParse(employeeCount)`.
 */
export const signUpDetailsSchema = z.object({
  firstName: z.string().regex(NAME_REGEX, INVALID_NAME_MESSAGE),
  lastName: z.string().regex(NAME_REGEX, INVALID_NAME_MESSAGE),
  companyName: z.string().trim().min(1, 'Company name is required'),
  // Digits-only — matches Dart `int.tryParse`'s accepted set. `/^\d+$/` rejects the hex (`0x10`) and
  // exponent (`1e3`) forms that a bare `Number()` would otherwise accept.
  numberOfEmployees: z
    .string()
    .trim()
    .refine((v) => /^\d+$/.test(v), INVALID_EMPLOYEE_COUNT_MESSAGE),
});
export type SignUpDetailsFormValues = z.infer<typeof signUpDetailsSchema>;

/**
 * Sign-up email + password. Email format + the 3 password rules (webapp `_isActiveButton`). The
 * inline indicators read the live predicates above; this schema gates the submit button.
 */
export const signUpEmailPasswordSchema = z.object({
  email: z.string().regex(EMAIL_REGEX, INVALID_EMAIL_MESSAGE),
  password: z
    .string()
    .refine(passwordHasMinLength, 'Password must be at least 8 characters')
    .refine(passwordHasLettersAndNumbers, 'Password must contain letters and numbers')
    .refine(passwordHasSymbol, 'Password must contain a symbol'),
});
export type SignUpEmailPasswordFormValues = z.infer<typeof signUpEmailPasswordSchema>;

/**
 * The company console's invite page (EP-MNT17): the account step of a person new to Givmo. The
 * address is the invite's, never typed, so the form is the console sign-up's own name rule (the
 * details screen's) and its three password rules (the email screen's), with their words.
 */
export const inviteJoinSchema = z.object({
  firstName: z.string().regex(NAME_REGEX, INVALID_NAME_MESSAGE),
  lastName: z.string().regex(NAME_REGEX, INVALID_NAME_MESSAGE),
  password: z
    .string()
    .refine(passwordHasMinLength, 'Password must be at least 8 characters')
    .refine(passwordHasLettersAndNumbers, 'Password must contain letters and numbers')
    .refine(passwordHasSymbol, 'Password must contain a symbol'),
});
export type InviteJoinFormValues = z.infer<typeof inviteJoinSchema>;
